// organize-imports-ignore
import { FC, useCallback, useState, useMemo, useEffect } from 'react';
// third-party
import '@fullcalendar/react/dist/vdom';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import timelinePlugin from '@fullcalendar/timeline';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
// material-ui
import { Box, Button, Stack, Theme, Tooltip, useMediaQuery } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { addMonths, addWeeks, endOfMonth, format, startOfMonth, subWeeks } from 'date-fns';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import useAuth from 'hooks/useAuth';
import useUser from 'hooks/useUser';
import useUserCalendars from 'hooks/useUserCalendars';
import frLocale from '@fullcalendar/core/locales/fr';
// project imports
import MainCard from 'views/components/cards/MainCard';
import PopularCard from 'views/components/skeleton/PopularCard';
import { Appointment, NTCalendarEvent } from 'types/appointment';
import { useNavigate, useLocation, useParams } from 'react-router';
import { useMutation } from 'react-query';
import { getErrorMessage } from 'utils/stringUtils';
import { useSnackbar } from 'notistack';
import { FormattedMessage, useIntl } from 'react-intl';
import useProvider from 'hooks/useProvider';
import useOptions from 'hooks/useOptions';
import moment from 'moment-timezone';
import CalendarStyled from './CalendarStyled';
import Toolbar from './Toolbar';
// assets
import useAppointments from './useAppointments';
import useCalendar from './useCalendar';
import AppointmentDialogs from './AppointmentDialogs';
import ConnectionTestDialog from './ConnectionTestDialog';
import useGroups from './useGroups';
import usePatients from './usePatients';
import EditBlockOffTimeSlot from './EditBlockOffTimeSlot';
import RescheduleConfirmationDialog from './RescheduleConfirmationDialog';
import NewAnnouncementsDialog from 'views/components/modals/NewAnnouncementsDialog';
import { usePrevious } from 'react-use';

const useStyles = makeStyles((theme: Theme) => ({
  calendarItem: {
    marginRight: '8px',
    marginBottom: '4px',
    padding: '4px 8px',
    border: 'solid 1px #ccc',
    borderRadius: '4px',
    cursor: 'pointer'
  },
  calendarCircle: {
    width: 20,
    height: 20,
    marginRight: '4px',
    borderRadius: 20
  },
  profileName: {
    lineHeight: '14px'
  }
}));

// ==============================|| APPLICATION CALENDAR ||============================== //

export type Props = {
  userId: string;
  baseUrl?: string;
};

const AppointmentsCalendar: FC<Props> = ({ userId, baseUrl = '/appointments' }) => {
  const classes = useStyles();
  const [createInitialDate, setCreateInitialDate] = useState(new Date());
  const matchSm = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();
  const { provider } = useProvider(userId);
  const savedView = localStorage.getItem('calendarView') || 'dayGridMonth';

  const tz = provider?.settings.timezone || 'America/Vancouver';
  const { userId: currentUserId, request } = useAuth();
  const { user, isProvider, isClient } = useUser(userId);
  const { calendars } = useUserCalendars(currentUserId);
  const filteredCalendars = useMemo(() => calendars?.filter((calendar) => calendar.show), [calendars]);

  const [inActiveCalendarIds, setInActiveCalendarIds] = useState<string[]>([]);
  const [showConnectionTest, setShowConnectionTest] = useState(false);
  const [selectedNTEvent, setSelectedNTEvent] = useState<NTCalendarEvent | null>(null);
  const [rescheduleEvent, setRescheduleEvent] = useState<any>(null);

  const { getToolbarProps, getCalendarProps, setDate, view, date } = useCalendar(savedView);
  const from = subWeeks(startOfMonth(date), 1);
  // const to = addWeeks(endOfMonth(date), 1);
  const to = addMonths(endOfMonth(date), 1);
  const timezone = user?.settings?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const enableMultipleParticipants = provider?.userOptions?.enableMultipleParticipants;
  const { appointments, events, refetchAppointments } = useAppointments({ from, to, id: userId });

  const { userId: paramsUserId } = useParams();
  const queryParams = new URLSearchParams(useLocation().search);
  const therapistId = paramsUserId ?? queryParams.get('therapist') ?? userId;
  usePatients(therapistId);

  useGroups();
  usePatients();

  const previousLocale = usePrevious(intl.locale);

  useEffect(() => {
    if (previousLocale && intl.locale !== previousLocale) {
      refetchAppointments();
    }
  }, [intl.locale, previousLocale, refetchAppointments]);

  const handleAppointmentSuccess = useCallback(() => {
    refetchAppointments();
    navigate(baseUrl);
  }, [navigate, baseUrl, refetchAppointments]);

  const handleEventClick = (arg) => {
    if (arg.event.extendedProps.type === 'nt-managed') {
      setSelectedNTEvent(arg.event.extendedProps.event);
    } else if (arg.event.extendedProps.type === 'managed') {
      navigate(`${baseUrl}/${arg.event.extendedProps.appointment.event_id}`);
    }
  };

  const handleDateClick = (date, event) => {
    setDate(new Date(date));
    getToolbarProps().onChangeView('timeGridDay', date);
  };

  const handleCalendarItemClick = useCallback(
    (calendar) => {
      if (user?.provider?.isCalendar) {
        return;
      }

      if (inActiveCalendarIds.includes(calendar.calendar_id)) {
        setInActiveCalendarIds((prev) => prev.filter((it) => it !== calendar.calendar_id));
      } else {
        setInActiveCalendarIds((prev) => [...prev, calendar.calendar_id]);
      }
    },
    // eslint-disable-next-line
    [inActiveCalendarIds]
  );

  if (!appointments) {
    return <PopularCard />;
  }

  function renderEventContent(eventInfo) {
    if (user?.provider?.isCalendar) {
      return;
    }
    const repeatId = eventInfo?.event?.extendedProps?.appointment?.repeatId;

    return (
      <div style={{ overflow: 'hidden' }}>
        {!!repeatId && (
          <Tooltip
            title={intl.formatMessage(
              {
                defaultMessage: 'Recurring Appointment {repeatInfo}'
              },
              {
                repeatInfo: eventInfo?.event?.extendedProps?.appointment?.repeatInfo || ''
              }
            )}
          >
            <div
              style={{
                position: 'absolute',
                top: 1,
                right: 3,

                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              <EventRepeatIcon sx={{ width: 14, height: 14 }} />
            </div>
          </Tooltip>
        )}
        <div className="fc-event-time">{eventInfo.timeText} </div>
        <div className="fc-event-title-container">{eventInfo.event.title}</div>
      </div>
    );
  }

  return (
    <MainCard
      title={
        userId === currentUserId
          ? intl.formatMessage({ defaultMessage: 'Your Appointments' })
          : intl.formatMessage(
              { defaultMessage: 'Manage Appointments for {firstName} {lastName}' },
              { firstName: user?.firstName, lastName: user?.lastName }
            )
      }
      secondary={
        <Stack direction="row" spacing={2}>
          <Button
            color="primary"
            variant="contained"
            hidden={user?.provider?.isCalendar}
            onClick={() => setShowConnectionTest((prev) => !prev)}
            style={{ display: user?.provider?.isCalendar ? 'none' : '' }}
          >
            <FormattedMessage defaultMessage="Test Your Connection" />
          </Button>

          {isProvider && (
            <>
              <Button
                color="primary"
                variant="contained"
                style={{ display: user?.provider?.isCalendar ? 'none' : '' }}
                onClick={() => {
                  setCreateInitialDate(new Date());
                  navigate(`${baseUrl}/create`);
                }}
              >
                <FormattedMessage defaultMessage="New Session" />
              </Button>
              {enableMultipleParticipants && (
                <Button
                  color="secondary"
                  variant="contained"
                  style={{ display: user?.provider?.isCalendar ? 'none' : '' }}
                  onClick={() => {
                    setCreateInitialDate(new Date());
                    navigate(`${baseUrl}/create-group`);
                  }}
                >
                  <FormattedMessage defaultMessage="New Group Session" />
                </Button>
              )}
            </>
          )}
        </Stack>
      }
      sx={{ overflow: 'visible !important' }}
    >
      <CalendarStyled>
        {!!filteredCalendars?.length && (
          <Box py={2} display="flex" flexWrap="wrap">
            {filteredCalendars.map((calendar) => (
              <Box
                display="flex"
                alignItems="center"
                className={classes.calendarItem}
                sx={{ backgroundColor: inActiveCalendarIds.includes(calendar.calendar_id) ? '' : '#d9edf7' }}
                key={calendar.calendar_id}
                onClick={() => handleCalendarItemClick(calendar)}
              >
                <span className={classes.calendarCircle} style={{ backgroundColor: calendar.color ?? 'rgb(133, 88, 199)' }} />
                <span className={classes.profileName}>{calendar.calendar_name}</span>
              </Box>
            ))}
          </Box>
        )}

        <Toolbar {...getToolbarProps()} timezone={timezone} />
        <FullCalendar
          {...getCalendarProps()}
          locale={intl.locale === 'fr' ? frLocale : undefined}
          headerToolbar={false}
          eventContent={renderEventContent}
          events={events.filter((event) => !inActiveCalendarIds.includes(event?.extendedProps?.calendarId))}
          height={matchSm ? 'auto' : 850}
          selectLongPressDelay={1}
          scrollTime={'08:00:00'}
          eventClick={handleEventClick}
          navLinks
          navLinkDayClick={handleDateClick}
          select={({ start }) => {
            if (user?.provider?.isCalendar) {
              return;
            }
            if (isProvider) {
              setCreateInitialDate(start);
              navigate(`${baseUrl}/create`);
            } else if (user?.clientOptions?.canClientRequestAppointment) {
              navigate(`/availability/${provider?.id}/${format(start, 'yyyy-MM-dd')}`);
            }
          }}
          eventMinHeight={40}
          eventDrop={(arg) => {
            setRescheduleEvent({
              variables: {
                event_id: arg.event.extendedProps.appointment.event_id,
                date: moment(`${moment(arg.event.start).format('YYYY-MM-DD HH:mm:ss')}`).tz(tz)
              }
            });
          }}
          eventAllow={(dropInfo, draggedEvent) => {
            if (isClient) return false;
            if (!draggedEvent?.extendedProps?.appointment?.event_id) return false;
            if (draggedEvent?.extendedProps?.appointment?.isAssociateTherapist) return false;
            if (moment(draggedEvent.extendedProps.appointment.start).isBefore()) return false;

            return true;
          }}
          plugins={[momentTimezonePlugin, listPlugin, dayGridPlugin, timelinePlugin, timeGridPlugin, interactionPlugin]}
          timeZone={timezone}
          displayEventEnd={true}
        />
      </CalendarStyled>
      <AppointmentDialogs
        baseUrl={baseUrl}
        createInitialDate={createInitialDate}
        initializeTime={view !== 'dayGridMonth'}
        onSuccess={handleAppointmentSuccess}
        appointments={appointments as Appointment[]}
      />
      {showConnectionTest && <ConnectionTestDialog onClose={() => setShowConnectionTest(false)} />}
      {!!selectedNTEvent && (
        <EditBlockOffTimeSlot
          event={selectedNTEvent}
          onUpdateSuccess={handleAppointmentSuccess}
          onClose={() => setSelectedNTEvent(null)}
          events={(appointments as any[])?.filter((appointment) => appointment.ntCalendarEvent) as NTCalendarEvent[]}
        />
      )}
      {!!rescheduleEvent && (
        <RescheduleConfirmationDialog
          rescheduleEvent={rescheduleEvent}
          onClose={() => setRescheduleEvent(null)}
          refetchAppointments={refetchAppointments}
        />
      )}

      <NewAnnouncementsDialog />
    </MainCard>
  );
};

export default AppointmentsCalendar;
